import React, { useState, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useWorkouts } from "../../contexts/WorkoutContext";
import { useUserManagement } from "../../hooks/useUserManagement";
import TrainerRecords from "./TrainerRecords";
import TrainerWorkouts from "./TrainerWorkouts";
import MerchOrders from "./MerchOrders";
import Enquiries from "./Enquiries";
import Timer from "./Timer";
import PBToast from "./PBToast";
import {
	Home,
	Dumbbell,
	Users,
	Settings,
	LogOut,
	Edit3,
	Trash2,
	UserCheck,
	Key,
	Shield,
	ShieldOff,
	Activity,
	Timer as TimerIcon,
	ChevronDown,
	ChevronUp,
	Package,
	MessageSquare,
	ClipboardList,
	FlaskConical,
	CalendarDays,
	Mail,
	Wand2,
	Video,
	Plus,
	RotateCcw,
	Target,
} from "lucide-react";
import CustomModal from "./CustomModal";
import GymSettings from "./GymSettings";
import TrainerPlanner from "./TrainerPlanner";
import TrainerVideoLibrary from "./TrainerVideoLibrary";
import TrainerGoals from "./TrainerGoals";
import { sendJoiningInstructions } from "../../services/emailService";

const TrainerDashboard = () => {
	const {
		user,
		logout,
		createUser,
		updateUser,
		deleteUser,
		restoreUser,
		toggleUserStatus,
		toggleBetaTester,
		resetUserPassword,
		loading: authLoading,
	} = useAuth();

	const {
		sharedWorkouts,
		allUserRecords,
		userRecords,
		movements,
		countTypes,
		notifications,
		allWorkoutScores,
		addSharedWorkout,
		deleteSharedWorkout,
		addMovement,
		deleteMovement,
		addCountType,
		deleteCountType,
		addNotification,
		deleteNotification,
		addUserRecord,
		deleteUserRecord,
		plannerEntries,
		addPlannerEntry,
		updatePlannerEntry,
		deletePlannerEntry,
		workshops,
		addWorkshop,
		updateWorkshop,
		deleteWorkshop,
		toggleWorkshopPublish,
		allGoals,
		goalDeadline,
		setGoalDeadlineSetting,
		updateGoal,
		deleteGoal,
		loading: workoutsLoading,
		pbToast,
		setPbToast,
	} = useWorkouts();

	const { users, deletedUsers, loading: usersLoading } = useUserManagement();

	const dismissPbToast = useCallback(() => setPbToast(null), [setPbToast]);

	const [sidebarOpen, setSidebarOpen] = useState(false);
	const [activeTab, setActiveTab] = useState("dashboard"); // dashboard, workouts, users, records, timer
	const [showCreateUserForm, setShowCreateUserForm] = useState(false);
	const [showNotificationForm, setShowNotificationForm] = useState(false);
	const [expandedNotifications, setExpandedNotifications] = useState({});
	const [editingUser, setEditingUser] = useState(null);
	const [showPasswordReset, setShowPasswordReset] = useState(null);
	const [modal, setModal] = useState({
		show: false,
		title: "",
		message: "",
		type: "info",
	});
	const [confirmModal, setConfirmModal] = useState({
		show: false,
		title: "",
		message: "",
		type: "delete",
		onConfirm: null,
	});

	const toggleNotification = (notificationId) => {
		setExpandedNotifications((prev) => ({
			...prev,
			[notificationId]: !prev[notificationId],
		}));
	};

	const [newUser, setNewUser] = useState({
		name: "",
		email: "",
		password: "",
	});
	const [sendEmailOnCreate, setSendEmailOnCreate] = useState(true);
	const [sendingEmailTo, setSendingEmailTo] = useState(null); // uid of user currently being emailed

	const [editUserData, setEditUserData] = useState({
		name: "",
		email: "",
	});

	const [newPassword, setNewPassword] = useState("");

	const [newNotification, setNewNotification] = useState({
		title: "",
		content: "",
		type: "info",
	});

	const handleLogout = async () => {
		try {
			await logout();
		} catch (error) {
			console.error("Logout error:", error);
		}
	};

	const handleCreateUser = async (e) => {
		e.preventDefault();
		if (!newUser.name || !newUser.email || !newUser.password) {
			setModal({
				show: true,
				title: "Missing Fields",
				message: "Please fill in all fields.",
				type: "warning",
			});
			return;
		}

		try {
			await createUser(newUser);

			// Send joining instructions email if checkbox is ticked
			if (sendEmailOnCreate) {
				const emailResult = await sendJoiningInstructions({
					userName: newUser.name,
					userEmail: newUser.email,
					tempPassword: newUser.password,
					trainerName: user?.name,
				});

				if (!emailResult.success) {
					setModal({
						show: true,
						title: "User Created — Email Failed",
						message: `User was created successfully, but the joining email could not be sent: ${emailResult.error}. You can resend it from the user list.`,
						type: "warning",
					});
					setNewUser({ name: "", email: "", password: "" });
					setShowCreateUserForm(false);
					return;
				}
			}

			setNewUser({ name: "", email: "", password: "" });
			setShowCreateUserForm(false);
			setModal({
				show: true,
				title: "User Created",
				message: sendEmailOnCreate
					? "User created and joining instructions sent!"
					: "User created successfully!",
				type: "success",
			});
		} catch (error) {
			setModal({
				show: true,
				title: "Error",
				message: error.message,
				type: "error",
			});
		}
	};

	const handleEditUser = async (e) => {
		e.preventDefault();
		if (!editUserData.name || !editUserData.email) {
			setModal({
				show: true,
				title: "Missing Fields",
				message: "Please fill in all fields.",
				type: "warning",
			});
			return;
		}

		try {
			await updateUser(editingUser.uid, editUserData);
			setEditingUser(null);
			setEditUserData({ name: "", email: "" });
			setModal({
				show: true,
				title: "User Updated",
				message: "User updated successfully!",
				type: "success",
			});
		} catch (error) {
			setModal({
				show: true,
				title: "Error",
				message: error.message,
				type: "error",
			});
		}
	};

	const startEditUser = (user) => {
		setEditingUser(user);
		setEditUserData({ name: user.name, email: user.email });
	};

	const handleDeleteUser = async (uid, userName) => {
		setConfirmModal({
			show: true,
			title: "Delete User",
			message: `Are you sure you want to delete ${userName}? They will be moved to the deleted list and can be restored later.`,
			type: "delete",
			onConfirm: async () => {
				setConfirmModal((prev) => ({ ...prev, show: false }));
				try {
					await deleteUser(uid);
					setModal({
						show: true,
						title: "User Deleted",
						message:
							"User has been removed. You can restore them from the deleted users list.",
						type: "success",
					});
				} catch (error) {
					setModal({
						show: true,
						title: "Error",
						message: error.message,
						type: "error",
					});
				}
			},
		});
	};

	const handleRestoreUser = async (uid, userName) => {
		setConfirmModal({
			show: true,
			title: "Restore User",
			message: `Restore ${userName}? They will be reactivated and able to log in again.`,
			type: "warning",
			onConfirm: async () => {
				setConfirmModal((prev) => ({ ...prev, show: false }));
				try {
					await restoreUser(uid);
					setModal({
						show: true,
						title: "User Restored",
						message: `${userName} has been restored and reactivated.`,
						type: "success",
					});
				} catch (error) {
					setModal({
						show: true,
						title: "Error",
						message: error.message,
						type: "error",
					});
				}
			},
		});
	};

	const handleToggleUserStatus = async (uid, currentStatus, userName) => {
		const action = currentStatus ? "deactivate" : "activate";
		setConfirmModal({
			show: true,
			title: `${action.charAt(0).toUpperCase() + action.slice(1)} User`,
			message: `Are you sure you want to ${action} ${userName}?`,
			type: "warning",
			onConfirm: async () => {
				setConfirmModal((prev) => ({ ...prev, show: false }));
				try {
					await toggleUserStatus(uid);
					setModal({
						show: true,
						title: "Success",
						message: `User ${action}d successfully!`,
						type: "success",
					});
				} catch (error) {
					setModal({
						show: true,
						title: "Error",
						message: error.message,
						type: "error",
					});
				}
			},
		});
	};

	const handleToggleBetaTester = async (uid, currentBetaStatus, userName) => {
		try {
			await toggleBetaTester(uid);
			setModal({
				show: true,
				title: "Beta Access Updated",
				message: `${userName} ${currentBetaStatus ? "removed from" : "added to"} beta testers.`,
				type: "success",
			});
		} catch (error) {
			setModal({
				show: true,
				title: "Error",
				message: error.message,
				type: "error",
			});
		}
	};

	const handleCreateNotification = async (e) => {
		e.preventDefault();
		if (!newNotification.title || !newNotification.content) {
			setModal({
				show: true,
				title: "Missing Fields",
				message: "Please fill in title and content.",
				type: "warning",
			});
			return;
		}

		try {
			await addNotification(newNotification);
			setNewNotification({ title: "", content: "", type: "info" });
			setShowNotificationForm(false);
			setModal({
				show: true,
				title: "Notification Posted",
				message: "Notification posted successfully!",
				type: "success",
			});
		} catch (error) {
			setModal({
				show: true,
				title: "Error",
				message: error.message || "Failed to post notification.",
				type: "error",
			});
		}
	};

	const handleDeleteNotification = async (notificationId) => {
		setConfirmModal({
			show: true,
			title: "Delete Notification",
			message:
				"Are you sure you want to delete this notification? This action cannot be undone.",
			type: "delete",
			onConfirm: async () => {
				setConfirmModal((prev) => ({ ...prev, show: false }));
				try {
					await deleteNotification(notificationId);
					setModal({
						show: true,
						title: "Deleted",
						message: "Notification deleted successfully!",
						type: "success",
					});
				} catch (error) {
					setModal({
						show: true,
						title: "Error",
						message: error.message || "Failed to delete notification.",
						type: "error",
					});
				}
			},
		});
	};

	const handlePasswordReset = async (e) => {
		e.preventDefault();
		if (!newPassword || newPassword.length < 6) {
			setModal({
				show: true,
				title: "Invalid Password",
				message: "Password must be at least 6 characters long.",
				type: "warning",
			});
			return;
		}

		try {
			await resetUserPassword(showPasswordReset.uid, newPassword);
			setShowPasswordReset(null);
			setNewPassword("");
			setModal({
				show: true,
				title: "Password Reset",
				message: "Password reset successfully!",
				type: "success",
			});
		} catch (error) {
			setModal({
				show: true,
				title: "Error",
				message: error.message,
				type: "error",
			});
		}
	};

	const handleSendJoiningEmail = async (targetUser) => {
		setSendingEmailTo(targetUser.uid);
		try {
			const result = await sendJoiningInstructions({
				userName: targetUser.name,
				userEmail: targetUser.email,
				tempPassword: null, // No password available for existing users
				trainerName: user?.name,
			});

			if (result.success) {
				setModal({
					show: true,
					title: "Email Sent",
					message: `Joining instructions sent to ${targetUser.email}`,
					type: "success",
				});
			} else {
				setModal({
					show: true,
					title: "Email Failed",
					message: `Could not send email: ${result.error}`,
					type: "error",
				});
			}
		} catch (error) {
			setModal({
				show: true,
				title: "Error",
				message: error.message || "Failed to send email.",
				type: "error",
			});
		}
		setSendingEmailTo(null);
	};

	// Handlers for movement and count type management
	const handleAddMovement = async (movementName) => {
		try {
			await addMovement(movementName);
			setModal({
				show: true,
				title: "Movement Added",
				message: "Movement added successfully!",
				type: "success",
			});
		} catch (error) {
			setModal({
				show: true,
				title: "Error",
				message: error.message,
				type: "error",
			});
		}
	};

	const handleDeleteMovement = async (movementName) => {
		try {
			await deleteMovement(movementName);
			setModal({
				show: true,
				title: "Movement Deleted",
				message: "Movement deleted successfully!",
				type: "success",
			});
		} catch (error) {
			setModal({
				show: true,
				title: "Error",
				message: error.message,
				type: "error",
			});
		}
	};

	const handleAddCountType = async (countTypeData) => {
		try {
			await addCountType(countTypeData);
			setModal({
				show: true,
				title: "Type Added",
				message: "Movement type added successfully!",
				type: "success",
			});
		} catch (error) {
			setModal({
				show: true,
				title: "Error",
				message: error.message,
				type: "error",
			});
		}
	};

	const handleDeleteCountType = async (countTypeValue) => {
		try {
			await deleteCountType(countTypeValue);
			setModal({
				show: true,
				title: "Type Deleted",
				message: "Movement type deleted successfully!",
				type: "success",
			});
		} catch (error) {
			setModal({
				show: true,
				title: "Error",
				message: error.message,
				type: "error",
			});
		}
	};

	const sidebarItems = [
		{ icon: Home, label: "Dashboard", tab: "dashboard" },
		{ icon: ClipboardList, label: "Workouts", tab: "workouts" },
		{ icon: Dumbbell, label: "Movements", tab: "records" },
		{ icon: Target, label: "Goals", tab: "goals" },
		{ icon: CalendarDays, label: "Planner", tab: "planner" },
		{ icon: Video, label: "Video Library", tab: "workshops" },
		{ icon: TimerIcon, label: "Timer", tab: "timer" },
		{ icon: MessageSquare, label: "Enquiries", tab: "enquiries" },
		{ icon: Package, label: "Orders", tab: "orders" },
		{ icon: Users, label: "User Management", tab: "users" },
		{ icon: Settings, label: "Settings", tab: "settings" },
	];

	// Calculate stats
	const totalWorkouts = sharedWorkouts.length;
	const totalUsers = users.length;
	const activeUsers = users.filter((u) => u.isActive).length;

	// Records stats - exclude trainer's own records
	const memberRecords = allUserRecords.filter((r) => r.userId !== user?.uid);
	const totalRecords = memberRecords.length;
	const activeRecordUsers = new Set(memberRecords.map((r) => r.userId)).size;
	const uniqueMovements = new Set(memberRecords.map((r) => r.movement)).size;

	const loading = authLoading || workoutsLoading || usersLoading;

	return (
		<div className="dashboard-container">
			{/* Left Sidebar */}
			<div
				className={`dashboard-sidebar${
					sidebarOpen ? " dashboard-sidebar--open" : ""
				}`}
			>
				<div className="dashboard-sidebar-header">
					<img
						src={process.env.PUBLIC_URL + "/EU59_LogoWHTAsset 5@4x.png"}
						alt="Urban 59 Logo"
						className="dashboard-sidebar-logo"
						style={{
							maxWidth: "180px",
							height: "auto",
							display: "block",
							margin: "0 auto 0rem auto",
						}}
					/>
				</div>

				<nav className="dashboard-sidebar-nav">
					{sidebarItems.map((item, index) => {
						const IconComponent = item.icon;
						const handleClick = () => {
							if (item.tab) {
								setActiveTab(item.tab);
								setSidebarOpen(false);
							}
						};

						return (
							<button
								key={index}
								onClick={handleClick}
								className={`dashboard-nav-item ${
									item.tab && activeTab === item.tab ? "active" : ""
								}`}
								style={{
									background: "none",
									border: "none",
									width: "100%",
									textAlign: "left",
								}}
								disabled={!item.tab}
							>
								<span className="dashboard-nav-icon">
									<IconComponent size={20} />
								</span>
								<span className="dashboard-nav-label">{item.label}</span>
							</button>
						);
					})}
				</nav>

				<div className="dashboard-sidebar-footer">
					<button className="dashboard-logout-btn" onClick={handleLogout}>
						<span className="dashboard-nav-icon">
							<LogOut size={20} />
						</span>
						<span className="dashboard-nav-label">Logout</span>
					</button>
				</div>
			</div>

			{/* Main Content */}
			<div className="dashboard-main-content">
				{/* Top Header */}
				<div className="dashboard-top-header">
					{!sidebarOpen && (
						<button
							className="dashboard-sidebar-toggle"
							onClick={() => setSidebarOpen((open) => !open)}
							aria-label="Toggle sidebar"
						>
							<span style={{ fontSize: 28, lineHeight: 1 }}>&#9776;</span>
						</button>
					)}
					{sidebarOpen && (
						<div
							className="dashboard-sidebar-backdrop"
							onClick={() => setSidebarOpen(false)}
						></div>
					)}
					<div className="dashboard-header-left">
						<p className="dashboard-page-subtitle">
							Trainer Dashboard - Welcome back, {user?.name}!
						</p>
					</div>
					<img
						src={process.env.PUBLIC_URL + "/EU59_LogoAsset 3@4x.png"}
						alt="Urban 59"
						className="dashboard-header-mobile-logo"
					/>
					<div className="dashboard-header-right">
						<div
							className="dashboard-user-profile"
							style={{ gap: 12, display: "flex", alignItems: "center" }}
						>
							<div
								className="dashboard-user-info"
								style={{ textAlign: "right" }}
							>
								<h6 className="dashboard-user-name" style={{ margin: 0 }}>
									{user?.name || "Trainer"}
								</h6>
								<small className="dashboard-user-email">{user?.email}</small>
							</div>
							<div className="dashboard-user-avatar">
								<span className="dashboard-user-initial">
									{user?.name?.charAt(0).toUpperCase() || "T"}
								</span>
							</div>
						</div>
					</div>
				</div>

				{/* Stats Cards */}
				<div className="dashboard-stats-grid">
					<div className="dashboard-stat-card primary">
						<div className="dashboard-stat-content">
							<div className="dashboard-stat-info">
								<h3 className="dashboard-stat-title">Total Users</h3>
								<h2 className="dashboard-stat-number">
									{loading ? "..." : totalUsers}
								</h2>
								<small className="dashboard-stat-change positive">
									{loading ? "..." : activeUsers} active
								</small>
							</div>
							<div className="dashboard-stat-icon">
								<Users size={32} />
							</div>
						</div>
					</div>

					<div className="dashboard-stat-card">
						<div className="dashboard-stat-content">
							<div className="dashboard-stat-info">
								<h3 className="dashboard-stat-title">
									{activeTab === "records"
										? "Total Movements"
										: "Total Workouts"}
								</h3>
								<h2 className="dashboard-stat-number">
									{loading
										? "..."
										: activeTab === "records"
											? totalRecords
											: totalWorkouts}
								</h2>
								<small className="dashboard-stat-change positive">
									{activeTab === "records"
										? "Personal movements"
										: "Active programs"}
								</small>
							</div>
							<div className="dashboard-stat-icon">
								{activeTab === "records" ? (
									<Dumbbell size={32} />
								) : (
									<ClipboardList size={32} />
								)}
							</div>
						</div>
					</div>

					<div className="dashboard-stat-card">
						<div className="dashboard-stat-content">
							<div className="dashboard-stat-info">
								<h3 className="dashboard-stat-title">
									{activeTab === "records"
										? "Active Movement Users"
										: "Total Notifications"}
								</h3>
								<h2 className="dashboard-stat-number">
									{loading
										? "..."
										: activeTab === "records"
											? activeRecordUsers
											: notifications.length}
								</h2>
								<small className="dashboard-stat-change positive">
									{activeTab === "records"
										? "Users tracking movements"
										: "Posted announcements"}
								</small>
							</div>
							<div className="dashboard-stat-icon">
								<UserCheck size={32} />
							</div>
						</div>
					</div>

					<div className="dashboard-stat-card">
						<div className="dashboard-stat-content">
							<div className="dashboard-stat-info">
								<h3 className="dashboard-stat-title">
									{activeTab === "records"
										? "Movements Tracked"
										: "Active Users"}
								</h3>
								<h2 className="dashboard-stat-number">
									{loading
										? "..."
										: activeTab === "records"
											? uniqueMovements
											: activeUsers}
								</h2>
								<small className="dashboard-stat-change positive">
									{activeTab === "records"
										? "Different exercises"
										: `${activeUsers} of ${totalUsers} users`}
								</small>
							</div>
							<div className="dashboard-stat-icon">
								{activeTab === "records" ? (
									<Activity size={32} />
								) : (
									<Users size={32} />
								)}
							</div>
						</div>
					</div>
				</div>

				{/* Content */}
				<div className="dashboard-content-grid">
					<div className="dashboard-content-main">
						{/* Movements Management Tab */}
						{activeTab === "records" && (
							<TrainerRecords
								allUserRecords={memberRecords}
								users={users}
								movements={movements}
								countTypes={countTypes}
								userRecords={userRecords}
								onAddMovement={handleAddMovement}
								onDeleteMovement={handleDeleteMovement}
								onAddCountType={handleAddCountType}
								onDeleteCountType={handleDeleteCountType}
								onAddRecord={addUserRecord}
								onDeleteRecord={deleteUserRecord}
								loading={loading}
							/>
						)}

						{/* Timer Tab */}
						{activeTab === "timer" && (
							<div className="card">
								<div className="card-header">
									<h5 className="mb-0">
										<TimerIcon className="me-2" size={20} />
										Workout Timer
									</h5>
								</div>
								<div className="card-body">
									<Timer />
								</div>
							</div>
						)}

						{/* Planner Tab */}
						{activeTab === "planner" && (
							<TrainerPlanner
								plannerEntries={plannerEntries}
								userRecords={userRecords}
								movements={movements}
								countTypes={countTypes}
								onAddEntry={addPlannerEntry}
								onUpdateEntry={updatePlannerEntry}
								onDeleteEntry={deletePlannerEntry}
								onAddRecord={addUserRecord}
								onDeleteRecord={deleteUserRecord}
								loading={loading}
							/>
						)}

						{/* Video Library Tab */}
						{activeTab === "workshops" && (
							<TrainerVideoLibrary
								workshops={workshops}
								onAdd={addWorkshop}
								onUpdate={updateWorkshop}
								onDelete={deleteWorkshop}
								onTogglePublish={toggleWorkshopPublish}
								loading={loading}
							/>
						)}

						{/* Goals Tab */}
						{activeTab === "goals" && (
							<TrainerGoals
								allGoals={allGoals}
								goalDeadline={goalDeadline}
								users={users}
								onSetDeadline={setGoalDeadlineSetting}
								onUpdateGoal={updateGoal}
								onDeleteGoal={deleteGoal}
								loading={loading}
							/>
						)}

						{/* Orders Tab */}
						{activeTab === "orders" && <MerchOrders loading={loading} />}

						{/* Enquiries Tab */}
						{activeTab === "enquiries" && (
							<Enquiries
								loading={loading}
								onMoveToUsers={(client) => {
									setNewUser({
										name: client.clientName,
										email: client.email || "",
										password: "",
									});
									setActiveTab("users");
									setShowCreateUserForm(true);
								}}
							/>
						)}

						{/* Dashboard Tab - News & Notifications */}
						{activeTab === "dashboard" && (
							<div className="card" style={{ overflow: "hidden" }}>
								<div className="card-header d-flex justify-content-between align-items-center">
									<h5 className="mb-0">Notifications</h5>
									<button
										className="btn btn-primary btn-sm"
										onClick={() =>
											setShowNotificationForm(!showNotificationForm)
										}
										disabled={loading}
									>
										{showNotificationForm ? (
											"Cancel"
										) : (
											<>
												<Plus size={16} className="me-1" />
												Post
											</>
										)}
									</button>
								</div>

								<div className="card-body" style={{ overflow: "hidden" }}>
									{showNotificationForm && (
										<div className="mb-4 p-3 bg-light rounded workshop-form-mobile">
											<h6>Post New Notification</h6>
											<form onSubmit={handleCreateNotification}>
												<div className="row g-3">
													<div className="col-12 col-md-8">
														<label className="form-label">Title *</label>
														<input
															type="text"
															className="form-control"
															value={newNotification.title}
															onChange={(e) =>
																setNewNotification({
																	...newNotification,
																	title: e.target.value,
																})
															}
															placeholder="Enter notification title"
															disabled={loading}
															required
														/>
													</div>
													<div className="col-12 col-md-4">
														<label className="form-label">Type</label>
														<select
															className="form-select"
															value={newNotification.type}
															onChange={(e) =>
																setNewNotification({
																	...newNotification,
																	type: e.target.value,
																})
															}
															disabled={loading}
														>
															<option value="info">Info</option>
															<option value="important">Important</option>
															<option value="urgent">Urgent</option>
														</select>
													</div>
													<div className="col-12">
														<label className="form-label">Content *</label>
														<textarea
															className="form-control"
															rows="4"
															value={newNotification.content}
															onChange={(e) =>
																setNewNotification({
																	...newNotification,
																	content: e.target.value,
																})
															}
															placeholder="Enter notification content..."
															disabled={loading}
															required
														/>
													</div>
													<div className="col-12">
														<div className="d-flex gap-2">
															<button
																type="submit"
																className="btn btn-success flex-grow-1 flex-md-grow-0"
																disabled={loading}
															>
																{loading ? "Posting..." : "Post Notification"}
															</button>
															<button
																type="button"
																className="btn btn-secondary flex-grow-1 flex-md-grow-0"
																onClick={() => setShowNotificationForm(false)}
																disabled={loading}
															>
																Cancel
															</button>
														</div>
													</div>
												</div>
											</form>
										</div>
									)}

									<h6 className="mb-3">
										Current Notifications ({notifications.length})
									</h6>
									{notifications.length === 0 ? (
										<div className="text-center py-5">
											<p className="text-muted">
												No notifications posted yet. Create your first
												notification to keep users informed!
											</p>
										</div>
									) : (
										<div>
											{notifications.map((notification) => {
												const isExpanded =
													expandedNotifications[notification.id];
												const typeBadgeStyle =
													notification.type === "urgent"
														? { backgroundColor: "#dc3545" }
														: notification.type === "important"
															? { backgroundColor: "#ffc107", color: "#1a1a1a" }
															: { backgroundColor: "#d15a36" };

												return (
													<div
														key={notification.id}
														className="mb-3"
														style={{
															borderRadius: "8px",
															overflow: "hidden",
															border: isExpanded
																? "1px solid #d15a36"
																: "1px solid #333",
															backgroundColor: "#1a1a1a",
															transition: "border-color 0.2s ease",
														}}
													>
														{/* Header */}
														<div
															className="p-3"
															onClick={() =>
																toggleNotification(notification.id)
															}
															style={{
																cursor: "pointer",
																borderBottom: isExpanded
																	? "2px solid #d15a36"
																	: "none",
															}}
														>
															<div className="d-flex justify-content-between align-items-start gap-2 mb-1">
																<div style={{ flex: 1, minWidth: 0 }}>
																	<h6
																		className="mb-0"
																		style={{
																			color: "#fff",
																			wordBreak: "break-word",
																			fontSize: "1rem",
																		}}
																	>
																		{notification.title}
																	</h6>
																</div>
																<div className="d-flex align-items-center gap-2 flex-shrink-0">
																	<button
																		className="btn btn-sm p-1"
																		onClick={(e) => {
																			e.stopPropagation();
																			handleDeleteNotification(notification.id);
																		}}
																		disabled={loading}
																		title="Delete notification"
																		style={{
																			backgroundColor: "rgba(220,53,69,0.15)",
																			color: "#dc3545",
																			border: "none",
																			lineHeight: 1,
																		}}
																	>
																		<Trash2 size={14} />
																	</button>
																	{isExpanded ? (
																		<ChevronUp
																			size={18}
																			style={{ color: "#d15a36" }}
																		/>
																	) : (
																		<ChevronDown
																			size={18}
																			style={{ color: "rgba(255,255,255,0.5)" }}
																		/>
																	)}
																</div>
															</div>

															{/* Meta row */}
															<div className="d-flex flex-wrap align-items-center gap-2">
																<span
																	className="badge"
																	style={{
																		...typeBadgeStyle,
																		fontSize: "0.7rem",
																	}}
																>
																	{notification.type}
																</span>
																<small
																	style={{ color: "rgba(255,255,255,0.45)" }}
																>
																	{new Date(
																		notification.createdAt,
																	).toLocaleDateString()}
																</small>
																<small
																	style={{ color: "rgba(255,255,255,0.35)" }}
																>
																	by {notification.createdBy}
																</small>
															</div>
														</div>

														{/* Expanded content */}
														{isExpanded && (
															<div
																className="p-3"
																style={{ overflow: "hidden" }}
															>
																<p
																	className="mb-0"
																	style={{
																		color: "rgba(255,255,255,0.7)",
																		fontSize: "0.9rem",
																		lineHeight: 1.6,
																		wordBreak: "break-word",
																	}}
																>
																	{notification.content}
																</p>
															</div>
														)}
													</div>
												);
											})}
										</div>
									)}
								</div>
							</div>
						)}

						{/* Workouts Tab */}
						{activeTab === "workouts" && (
							<TrainerWorkouts
								sharedWorkouts={sharedWorkouts}
								allWorkoutScores={allWorkoutScores}
								addSharedWorkout={addSharedWorkout}
								deleteSharedWorkout={deleteSharedWorkout}
								loading={loading}
							/>
						)}

						{/* User Management Tab */}
						{activeTab === "users" && (
							<div className="card">
								<div className="card-header d-flex justify-content-between align-items-center">
									<h5 className="mb-0">User Management</h5>
									<button
										className="btn btn-primary btn-sm"
										onClick={() => setShowCreateUserForm(!showCreateUserForm)}
										disabled={loading}
									>
										{showCreateUserForm ? "Cancel" : "Add User"}
									</button>
								</div>

								<div className="card-body">
									{showCreateUserForm && (
										<div className="mb-4 p-3 bg-light rounded create-user-form-mobile">
											<h6>Create New User</h6>
											<form onSubmit={handleCreateUser}>
												<div className="row g-3">
													<div className="col-12 col-md-6">
														<label className="form-label">Full Name *</label>
														<input
															type="text"
															className="form-control"
															value={newUser.name}
															onChange={(e) =>
																setNewUser({ ...newUser, name: e.target.value })
															}
															placeholder="Enter full name"
															disabled={loading}
															required
														/>
													</div>
													<div className="col-12 col-md-6">
														<label className="form-label">Email *</label>
														<input
															type="email"
															className="form-control"
															value={newUser.email}
															onChange={(e) =>
																setNewUser({
																	...newUser,
																	email: e.target.value,
																})
															}
															placeholder="Enter email address"
															disabled={loading}
															required
														/>
													</div>
													<div className="col-12 col-md-6">
														<label className="form-label">Password *</label>
														<div className="input-group">
															<input
																type="text"
																className="form-control"
																value={newUser.password}
																onChange={(e) =>
																	setNewUser({
																		...newUser,
																		password: e.target.value,
																	})
																}
																minLength="6"
																disabled={loading}
																placeholder="Enter or generate password"
																required
															/>
															<button
																type="button"
																className="btn btn-outline-primary"
																onClick={() => {
																	if (!newUser.name.trim()) {
																		setModal({
																			show: true,
																			title: "Name Required",
																			message:
																				"Please enter the user's full name first so a password can be generated.",
																			type: "warning",
																		});
																		return;
																	}
																	const generated =
																		newUser.name
																			.trim()
																			.toLowerCase()
																			.replace(/\s+/g, "") + "59";
																	setNewUser({
																		...newUser,
																		password: generated,
																	});
																}}
																disabled={loading}
																title="Generate password from name"
															>
																<Wand2 size={16} className="me-1" />
																<span className="d-none d-sm-inline">
																	Generate
																</span>
															</button>
														</div>
														<small className="text-muted">
															Generate sets password to name (lowercase, no
															spaces) + 59
														</small>
													</div>
													<div className="col-12">
														<div className="form-check mb-3">
															<input
																className="form-check-input"
																type="checkbox"
																id="sendJoiningEmail"
																checked={sendEmailOnCreate}
																onChange={(e) =>
																	setSendEmailOnCreate(e.target.checked)
																}
																disabled={loading}
															/>
															<label
																className="form-check-label"
																htmlFor="sendJoiningEmail"
															>
																<Mail size={14} className="me-1" />
																Send joining instructions email
															</label>
														</div>
														<div className="d-flex gap-2">
															<button
																type="submit"
																className="btn btn-success flex-grow-1 flex-md-grow-0"
																disabled={loading}
															>
																{loading ? "Creating..." : "Create User"}
															</button>
															<button
																type="button"
																className="btn btn-secondary flex-grow-1 flex-md-grow-0"
																onClick={() => setShowCreateUserForm(false)}
																disabled={loading}
															>
																Cancel
															</button>
														</div>
													</div>
												</div>
											</form>
										</div>
									)}

									{/* Edit User Modal */}
									{editingUser && (
										<div className="mb-4 p-3 bg-warning bg-opacity-10 rounded border border-warning">
											<h6>Edit User: {editingUser.name}</h6>
											<div className="row g-3">
												<div className="col-12 col-md-6">
													<label className="form-label">Full Name *</label>
													<input
														type="text"
														className="form-control"
														value={editUserData.name}
														onChange={(e) =>
															setEditUserData({
																...editUserData,
																name: e.target.value,
															})
														}
														disabled={loading}
													/>
												</div>
												<div className="col-12 col-md-6">
													<label className="form-label">Email *</label>
													<input
														type="email"
														className="form-control"
														value={editUserData.email}
														onChange={(e) =>
															setEditUserData({
																...editUserData,
																email: e.target.value,
															})
														}
														disabled={loading}
													/>
												</div>
												<div className="col-12">
													<div className="d-flex gap-2">
														<button
															type="button"
															className="btn btn-warning flex-grow-1 flex-md-grow-0"
															onClick={handleEditUser}
															disabled={loading}
														>
															{loading ? "Updating..." : "Update User"}
														</button>
														<button
															type="button"
															className="btn btn-secondary flex-grow-1 flex-md-grow-0"
															onClick={() => {
																setEditingUser(null);
																setEditUserData({ name: "", email: "" });
															}}
															disabled={loading}
														>
															Cancel
														</button>
													</div>
												</div>
											</div>
										</div>
									)}

									{/* Password Reset Modal */}
									{showPasswordReset && (
										<div className="mb-4 p-3 bg-info bg-opacity-10 rounded border border-info">
											<h6>Reset Password: {showPasswordReset.name}</h6>
											<div className="alert alert-info">
												<small>
													Note: In production, users would receive password
													reset emails instead.
												</small>
											</div>
											<div className="row g-3">
												<div className="col-12 col-md-6">
													<label className="form-label">New Password *</label>
													<input
														type="password"
														className="form-control"
														value={newPassword}
														onChange={(e) => setNewPassword(e.target.value)}
														minLength="6"
														disabled={loading}
													/>
												</div>
												<div className="col-12">
													<div className="d-flex gap-2">
														<button
															type="button"
															className="btn btn-info flex-grow-1 flex-md-grow-0"
															onClick={handlePasswordReset}
															disabled={loading}
														>
															{loading ? "Resetting..." : "Reset Password"}
														</button>
														<button
															type="button"
															className="btn btn-secondary flex-grow-1 flex-md-grow-0"
															onClick={() => {
																setShowPasswordReset(null);
																setNewPassword("");
															}}
															disabled={loading}
														>
															Cancel
														</button>
													</div>
												</div>
											</div>
										</div>
									)}

									{/* Users List */}
									<div>
										<h6 className="mb-3">
											Registered Users ({loading ? "..." : users.length})
										</h6>
										{loading ? (
											<div className="text-center py-4">
												<div
													className="spinner-border text-primary"
													role="status"
												>
													<span className="visually-hidden">Loading...</span>
												</div>
											</div>
										) : users.length === 0 ? (
											<div className="text-center py-4">
												<p className="text-muted">No users registered yet.</p>
												<p className="text-muted">
													Create your first user to get started!
												</p>
											</div>
										) : (
											<>
												{/* Desktop table — hidden on small screens */}
												<div className="table-responsive d-none d-md-block">
													<table className="table table-hover">
														<thead>
															<tr>
																<th>Name</th>
																<th>Email</th>
																<th>Status</th>
																<th>Beta</th>
																<th>Created</th>
																<th>Last Login</th>
																<th>Actions</th>
															</tr>
														</thead>
														<tbody>
															{users.map((user) => (
																<tr key={user.uid}>
																	<td>
																		<div className="d-flex align-items-center">
																			<div
																				className="dashboard-user-avatar me-2"
																				style={{
																					width: "32px",
																					height: "32px",
																					fontSize: "14px",
																				}}
																			>
																				{user.name.charAt(0).toUpperCase()}
																			</div>
																			{user.name}
																		</div>
																	</td>
																	<td>{user.email}</td>
																	<td>
																		<span
																			className={`badge ${
																				user.isActive
																					? "bg-success"
																					: "bg-danger"
																			}`}
																		>
																			{user.isActive ? "Active" : "Inactive"}
																		</span>
																	</td>
																	<td>
																		<span
																			className={`badge ${
																				user.isBetaTester
																					? "bg-info"
																					: "bg-secondary"
																			}`}
																			style={{ cursor: "pointer" }}
																			onClick={() =>
																				handleToggleBetaTester(
																					user.uid,
																					user.isBetaTester,
																					user.name,
																				)
																			}
																			title={
																				user.isBetaTester
																					? "Remove beta access"
																					: "Grant beta access"
																			}
																		>
																			<FlaskConical
																				size={12}
																				className="me-1"
																			/>
																			{user.isBetaTester ? "Beta" : "Standard"}
																		</span>
																	</td>
																	<td>
																		{new Date(
																			user.createdAt,
																		).toLocaleDateString()}
																	</td>
																	<td>
																		{user.lastLogin
																			? new Date(
																					user.lastLogin,
																				).toLocaleDateString()
																			: "Never"}
																	</td>
																	<td>
																		<div className="btn-group btn-group-sm">
																			<button
																				className="btn btn-outline-primary"
																				onClick={() => startEditUser(user)}
																				title="Edit User"
																				disabled={loading}
																			>
																				<Edit3 size={14} />
																			</button>
																			<button
																				className="btn btn-outline-secondary"
																				onClick={() =>
																					handleSendJoiningEmail(user)
																				}
																				title="Send Joining Instructions"
																				disabled={
																					loading || sendingEmailTo === user.uid
																				}
																			>
																				{sendingEmailTo === user.uid ? (
																					<span
																						className="spinner-border spinner-border-sm"
																						role="status"
																					/>
																				) : (
																					<Mail size={14} />
																				)}
																			</button>
																			<button
																				className="btn btn-outline-info"
																				onClick={() =>
																					setShowPasswordReset(user)
																				}
																				title="Reset Password"
																				disabled={loading}
																			>
																				<Key size={14} />
																			</button>
																			<button
																				className={`btn ${
																					user.isActive
																						? "btn-outline-warning"
																						: "btn-outline-success"
																				}`}
																				onClick={() =>
																					handleToggleUserStatus(
																						user.uid,
																						user.isActive,
																						user.name,
																					)
																				}
																				title={
																					user.isActive
																						? "Deactivate"
																						: "Activate"
																				}
																				disabled={loading}
																			>
																				{user.isActive ? (
																					<ShieldOff size={14} />
																				) : (
																					<Shield size={14} />
																				)}
																			</button>
																			<button
																				className="btn btn-outline-danger"
																				onClick={() =>
																					handleDeleteUser(user.uid, user.name)
																				}
																				title="Delete User"
																				disabled={loading}
																			>
																				<Trash2 size={14} />
																			</button>
																		</div>
																	</td>
																</tr>
															))}
														</tbody>
													</table>
												</div>

												{/* Mobile card layout — visible only on small screens */}
												<div className="d-md-none">
													{users.map((user) => (
														<div
															key={user.uid}
															className="mb-3 p-3"
															style={{
																backgroundColor: "#1a1a1a",
																borderRadius: "8px",
																border: "1px solid #333",
															}}
														>
															{/* Name + status row */}
															<div className="d-flex justify-content-between align-items-start mb-2">
																<div
																	className="d-flex align-items-center"
																	style={{ minWidth: 0, flex: 1 }}
																>
																	<div
																		className="dashboard-user-avatar me-2"
																		style={{
																			width: "36px",
																			height: "36px",
																			fontSize: "15px",
																			flexShrink: 0,
																		}}
																	>
																		{user.name.charAt(0).toUpperCase()}
																	</div>
																	<div style={{ minWidth: 0 }}>
																		<div
																			style={{
																				color: "#fff",
																				fontWeight: 600,
																				wordBreak: "break-word",
																			}}
																		>
																			{user.name}
																		</div>
																		<small
																			style={{
																				color: "rgba(255,255,255,0.5)",
																				wordBreak: "break-all",
																			}}
																		>
																			{user.email}
																		</small>
																	</div>
																</div>
																<div className="d-flex gap-1 flex-shrink-0 ms-2">
																	<span
																		className={`badge ${user.isActive ? "bg-success" : "bg-danger"}`}
																		style={{ fontSize: "0.7rem" }}
																	>
																		{user.isActive ? "Active" : "Inactive"}
																	</span>
																	<span
																		className={`badge ${user.isBetaTester ? "bg-info" : "bg-secondary"}`}
																		style={{
																			fontSize: "0.7rem",
																			cursor: "pointer",
																		}}
																		onClick={() =>
																			handleToggleBetaTester(
																				user.uid,
																				user.isBetaTester,
																				user.name,
																			)
																		}
																	>
																		{user.isBetaTester ? "Beta" : "Std"}
																	</span>
																</div>
															</div>

															{/* Dates */}
															<div
																className="d-flex flex-wrap gap-3 mb-2"
																style={{
																	fontSize: "0.8rem",
																	color: "rgba(255,255,255,0.45)",
																}}
															>
																<span>
																	Joined{" "}
																	{new Date(
																		user.createdAt,
																	).toLocaleDateString()}
																</span>
																<span>
																	Last login:{" "}
																	{user.lastLogin
																		? new Date(
																				user.lastLogin,
																			).toLocaleDateString()
																		: "Never"}
																</span>
															</div>

															{/* Action buttons */}
															<div className="d-flex flex-wrap gap-2">
																<button
																	className="btn btn-sm"
																	onClick={() => startEditUser(user)}
																	disabled={loading}
																	style={{
																		backgroundColor: "rgba(255,255,255,0.1)",
																		color: "#fff",
																		border: "none",
																	}}
																>
																	<Edit3 size={14} className="me-1" />
																	Edit
																</button>
																<button
																	className="btn btn-sm"
																	onClick={() => handleSendJoiningEmail(user)}
																	disabled={
																		loading || sendingEmailTo === user.uid
																	}
																	style={{
																		backgroundColor: "rgba(255,255,255,0.1)",
																		color: "#fff",
																		border: "none",
																	}}
																>
																	{sendingEmailTo === user.uid ? (
																		<span
																			className="spinner-border spinner-border-sm"
																			role="status"
																		/>
																	) : (
																		<>
																			<Mail size={14} className="me-1" />
																			Email
																		</>
																	)}
																</button>
																<button
																	className="btn btn-sm"
																	onClick={() => setShowPasswordReset(user)}
																	disabled={loading}
																	style={{
																		backgroundColor: "rgba(255,255,255,0.1)",
																		color: "#fff",
																		border: "none",
																	}}
																>
																	<Key size={14} className="me-1" />
																	Password
																</button>
																<button
																	className={`btn btn-sm`}
																	onClick={() =>
																		handleToggleUserStatus(
																			user.uid,
																			user.isActive,
																			user.name,
																		)
																	}
																	disabled={loading}
																	style={{
																		backgroundColor: user.isActive
																			? "rgba(255,193,7,0.15)"
																			: "rgba(25,135,84,0.15)",
																		color: user.isActive
																			? "#ffc107"
																			: "#198754",
																		border: "none",
																	}}
																>
																	{user.isActive ? (
																		<>
																			<ShieldOff size={14} className="me-1" />
																			Deactivate
																		</>
																	) : (
																		<>
																			<Shield size={14} className="me-1" />
																			Activate
																		</>
																	)}
																</button>
																<button
																	className="btn btn-sm"
																	onClick={() =>
																		handleDeleteUser(user.uid, user.name)
																	}
																	disabled={loading}
																	style={{
																		backgroundColor: "rgba(220,53,69,0.15)",
																		color: "#dc3545",
																		border: "none",
																	}}
																>
																	<Trash2 size={14} className="me-1" />
																	Delete
																</button>
															</div>
														</div>
													))}
												</div>
											</>
										)}
									</div>

									{/* Deleted Users Section */}
									{deletedUsers.length > 0 && (
										<div className="mt-4">
											<h6
												className="mb-3"
												style={{ color: "rgba(255,255,255,0.5)" }}
											>
												<Trash2 size={16} className="me-2" />
												Deleted Users ({deletedUsers.length})
											</h6>

											{/* Desktop table */}
											<div className="table-responsive d-none d-md-block">
												<table
													className="table table-hover"
													style={{ opacity: 0.7 }}
												>
													<thead>
														<tr>
															<th>Name</th>
															<th>Email</th>
															<th>Deleted</th>
															<th>Actions</th>
														</tr>
													</thead>
													<tbody>
														{deletedUsers.map((dUser) => (
															<tr key={dUser.uid}>
																<td>
																	<div className="d-flex align-items-center">
																		<div
																			className="dashboard-user-avatar me-2"
																			style={{
																				width: "32px",
																				height: "32px",
																				fontSize: "14px",
																				opacity: 0.5,
																			}}
																		>
																			{dUser.name.charAt(0).toUpperCase()}
																		</div>
																		{dUser.name}
																	</div>
																</td>
																<td>{dUser.email}</td>
																<td>
																	{dUser.deletedAt
																		? new Date(
																				dUser.deletedAt,
																			).toLocaleDateString()
																		: "Unknown"}
																</td>
																<td>
																	<button
																		className="btn btn-outline-success btn-sm"
																		onClick={() =>
																			handleRestoreUser(dUser.uid, dUser.name)
																		}
																		title="Restore User"
																		disabled={loading}
																	>
																		<RotateCcw size={14} className="me-1" />
																		Restore
																	</button>
																</td>
															</tr>
														))}
													</tbody>
												</table>
											</div>

											{/* Mobile cards */}
											<div className="d-md-none">
												{deletedUsers.map((dUser) => (
													<div
														key={dUser.uid}
														className="mb-3 p-3"
														style={{
															backgroundColor: "#1a1a1a",
															borderRadius: "8px",
															border: "1px solid #333",
															opacity: 0.7,
														}}
													>
														<div className="d-flex justify-content-between align-items-start mb-2">
															<div
																className="d-flex align-items-center"
																style={{ minWidth: 0, flex: 1 }}
															>
																<div
																	className="dashboard-user-avatar me-2"
																	style={{
																		width: "36px",
																		height: "36px",
																		fontSize: "15px",
																		flexShrink: 0,
																		opacity: 0.5,
																	}}
																>
																	{dUser.name.charAt(0).toUpperCase()}
																</div>
																<div style={{ minWidth: 0 }}>
																	<div
																		style={{
																			color: "#fff",
																			fontWeight: 600,
																			wordBreak: "break-word",
																		}}
																	>
																		{dUser.name}
																	</div>
																	<small
																		style={{
																			color: "rgba(255,255,255,0.5)",
																			wordBreak: "break-all",
																		}}
																	>
																		{dUser.email}
																	</small>
																</div>
															</div>
															<span
																className="badge bg-danger"
																style={{ fontSize: "0.7rem" }}
															>
																Deleted
															</span>
														</div>
														<div
															className="d-flex flex-wrap gap-3 mb-2"
															style={{
																fontSize: "0.8rem",
																color: "rgba(255,255,255,0.45)",
															}}
														>
															<span>
																Deleted{" "}
																{dUser.deletedAt
																	? new Date(
																			dUser.deletedAt,
																		).toLocaleDateString()
																	: "Unknown"}
															</span>
														</div>
														<button
															className="btn btn-sm"
															onClick={() =>
																handleRestoreUser(dUser.uid, dUser.name)
															}
															disabled={loading}
															style={{
																backgroundColor: "rgba(25,135,84,0.15)",
																color: "#198754",
																border: "none",
															}}
														>
															<RotateCcw size={14} className="me-1" />
															Restore
														</button>
													</div>
												))}
											</div>
										</div>
									)}
								</div>
							</div>
						)}

						{/* Settings Tab */}
						{activeTab === "settings" && <GymSettings loading={loading} />}
					</div>
				</div>
			</div>
			<CustomModal
				show={modal.show}
				title={modal.title}
				message={modal.message}
				type={modal.type}
				onClose={() => setModal({ ...modal, show: false })}
			/>
			<CustomModal
				show={confirmModal.show}
				title={confirmModal.title}
				message={confirmModal.message}
				type={confirmModal.type}
				onConfirm={confirmModal.onConfirm}
				onClose={() => setConfirmModal({ ...confirmModal, show: false })}
				confirmText={confirmModal.type === "delete" ? "Delete" : "Confirm"}
				loading={loading}
			/>
			{pbToast && (
				<PBToast
					movement={pbToast.movement}
					value={pbToast.value}
					unit={pbToast.unit}
					onDismiss={dismissPbToast}
				/>
			)}
		</div>
	);
};

export default TrainerDashboard;
